import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { Agency } from '@/data/models/agency.interface';
import { Client } from '@/data/models/client.interface';
import { ContractFilters } from '@/data/models/contract.interface';
import { DocumentMappingFilters } from '@/data/models/documentMapping.interface';
import { HeadingClientFilters } from '@/data/models/headingClient.interface';
import { OrderFilters } from '@/data/models/order.interface';
import { Platform } from '@/data/models/platform.interface';
import { ResourceFilters } from '@/data/models/resource.interface';
import { Tenant } from '@/data/models/tenant.interface';
import { areFiltersEqual } from '@/utils/filters.utils';

export interface BaseFavorite {
  id: string;
  type: 'client' | 'contracts' | 'orders' | 'resources' | 'heading_clients' | 'document_mapping';
  platform: Platform;
  tenant: Tenant | null;
  createdAt: string;
}

export interface ClientFavorite extends BaseFavorite {
  type: 'client';
  agency: Agency;
  client: Client;
}

export interface ContractsFavorite extends BaseFavorite {
  type: 'contracts';
  filters: ContractFilters;
  displayName?: string;
}

export interface OrdersFavorite extends BaseFavorite {
  type: 'orders';
  filters: OrderFilters;
  displayName?: string;
}

export interface ResourcesFavorite extends BaseFavorite {
  type: 'resources';
  filters: ResourceFilters;
  displayName?: string;
}

export interface HeadingClientsFavorite extends BaseFavorite {
  type: 'heading_clients';
  filters: HeadingClientFilters;
  displayName?: string;
}

export interface DocumentMappingFavorite extends BaseFavorite {
  type: 'document_mapping';
  filters: DocumentMappingFilters;
  displayName?: string;
}

export type FavoriteSearch =
  | ClientFavorite
  | ContractsFavorite
  | OrdersFavorite
  | ResourcesFavorite
  | HeadingClientsFavorite
  | DocumentMappingFavorite;

export interface FavoritesState {
  favorites: FavoriteSearch[];

  addClientFavorite: (
    _platform: Platform,
    _tenant: Tenant,
    _agency: Agency,
    _client: Client,
  ) => void;
  isClientFavorite: (
    _platformValue: string,
    _tenantValue: string,
    _agencyCode: string,
    _clientId: string,
  ) => boolean;

  addContractsFavorite: (
    _platform: Platform,
    _tenant: Tenant | null,
    _filters: ContractFilters,
    _displayName?: string,
  ) => void;
  isContractsFavorite: (
    _platformValue: string,
    _tenantValue: string,
    _filters: ContractFilters,
  ) => boolean;

  addOrdersFavorite: (
    _platform: Platform,
    _tenant: Tenant | null,
    _filters: OrderFilters,
    _displayName?: string,
  ) => void;
  isOrdersFavorite: (
    _platformValue: string,
    _tenantValue: string | null,
    _filters: OrderFilters,
  ) => boolean;

  addResourcesFavorite: (
    _platform: Platform,
    _tenant: Tenant | null,
    _filters: ResourceFilters,
    _displayName?: string,
  ) => void;
  isResourcesFavorite: (
    _platformValue: string,
    _tenantValue: string | null,
    _filters: ResourceFilters,
  ) => boolean;

  addHeadingClientsFavorite: (
    _platform: Platform,
    _filters: HeadingClientFilters,
    _displayName?: string,
  ) => void;
  isHeadingClientsFavorite: (
    _platformValue: string,
    _filters: HeadingClientFilters,
  ) => boolean;

  addDocumentMappingFavorite: (
    _platform: Platform,
    _filters: DocumentMappingFilters,
    _displayName?: string,
  ) => void;
  isDocumentMappingFavorite: (
    _platformValue: string,
    _filters: DocumentMappingFilters,
  ) => boolean;

  removeFavorite: (_id: string) => void;
  clearFavorites: () => void;
  getClientFavorites: () => ClientFavorite[];
  getContractsFavorites: () => ContractsFavorite[];
  getOrdersFavorites: () => OrdersFavorite[];
  getResourcesFavorites: () => ResourcesFavorite[];
  getHeadingClientsFavorites: () => HeadingClientsFavorite[];
  getDocumentMappingFavorites: () => DocumentMappingFavorite[];
}

// Migration function to update old filter field names
const migrateFavorites = (favorites: FavoriteSearch[]): FavoriteSearch[] => {
  return favorites.map((fav) => {
    if (fav.type === 'contracts') {
      const contractsFav = fav as ContractsFavorite;
      const filters = { ...contractsFav.filters };

      // Migrate old created_at_from to created_at
      if ('created_at_from' in filters) {
        filters.created_at = (
          filters as unknown as { created_at_from?: string }
        ).created_at_from;
        delete (filters as unknown as { created_at_from?: string })
          .created_at_from;
      }

      return {
        ...contractsFav,
        filters,
      };
    }
    return fav;
  });
};

const useFavoritesStore = create<FavoritesState>()(
  persist(
    (set, get) => ({
      favorites: [],

      // Client favorites
      addClientFavorite: (
        platform: Platform,
        tenant: Tenant,
        agency: Agency,
        client: Client,
      ) => {
        const state = get();
        const exists = state.favorites.some(
          (fav) =>
            fav.type === 'client' &&
            fav.platform?.value === platform?.value &&
            fav.tenant?.value === tenant?.value &&
            (fav as ClientFavorite).agency?.code_agence ===
              agency?.code_agence &&
            (fav as ClientFavorite).client?.id === client?.id,
        );

        if (!exists) {
          const newFavorite: ClientFavorite = {
            id: `client-${platform?.value}-${tenant?.value}-${agency?.code_agence}-${client?.id}-${Date.now()}`,
            type: 'client',
            platform,
            tenant,
            agency,
            client,
            createdAt: new Date().toISOString(),
          };
          set({ favorites: [...state.favorites, newFavorite] });
        }
      },

      isClientFavorite: (
        platformValue: string,
        tenantValue: string,
        agencyCode: string,
        clientId: string,
      ) => {
        const state = get();
        return state.favorites.some(
          (fav) =>
            fav.type === 'client' &&
            fav.platform?.value === platformValue &&
            fav.tenant?.value === tenantValue &&
            (fav as ClientFavorite).agency?.code_agence === agencyCode &&
            (fav as ClientFavorite).client?.id === clientId,
        );
      },

      addContractsFavorite: (
        platform: Platform,
        tenant: Tenant | null,
        filters: ContractFilters,
        displayName?: string,
      ) => {
        const state = get();

        const exists = state.favorites.some((fav) => {
          if (fav.type !== 'contracts') return false;
          const contractsFav = fav as ContractsFavorite;
          return (
            contractsFav.platform?.value === platform?.value &&
            contractsFav.tenant?.value === tenant?.value &&
            areFiltersEqual(
              contractsFav.filters as Record<string, unknown>,
              filters as Record<string, unknown>,
            )
          );
        });

        if (!exists) {
          const newFavorite: ContractsFavorite = {
            id: `contracts-${platform?.value}-${tenant?.value ?? 0}-${Date.now()}`,
            type: 'contracts',
            platform,
            tenant,
            filters,
            displayName,
            createdAt: new Date().toISOString(),
          };
          set({ favorites: [...state.favorites, newFavorite] });
        }
      },

      isContractsFavorite: (
        platformValue: string,
        tenantValue: string,
        filters: ContractFilters,
      ) => {
        const state = get();
        return state.favorites.some((fav) => {
          if (fav.type !== 'contracts') return false;
          const contractsFav = fav as ContractsFavorite;
          return (
            contractsFav.platform?.value === platformValue &&
            (contractsFav.tenant?.value === tenantValue ||
              tenantValue === '') &&
            areFiltersEqual(
              contractsFav.filters as Record<string, unknown>,
              filters as Record<string, unknown>,
            )
          );
        });
      },

      removeFavorite: (id: string) => {
        set((state) => ({
          favorites: state.favorites.filter((fav) => fav.id !== id),
        }));
      },

      clearFavorites: () => {
        set({ favorites: [] });
      },

      getClientFavorites: () => {
        const state = get();
        return state.favorites.filter(
          (fav) => fav.type === 'client',
        ) as ClientFavorite[];
      },

      getContractsFavorites: () => {
        const state = get();
        return state.favorites.filter(
          (fav) => fav.type === 'contracts',
        ) as ContractsFavorite[];
      },

      addOrdersFavorite: (
        platform: Platform,
        tenant: Tenant | null,
        filters: OrderFilters,
        displayName?: string,
      ) => {
        const state = get();

        const exists = state.favorites.some((fav) => {
          if (fav.type !== 'orders') return false;
          const ordersFav = fav as OrdersFavorite;
          return (
            ordersFav.platform?.value === platform?.value &&
            ordersFav.tenant?.value === tenant?.value &&
            areFiltersEqual(
              ordersFav.filters as Record<string, unknown>,
              filters as Record<string, unknown>,
            )
          );
        });

        if (!exists) {
          const newFavorite: OrdersFavorite = {
            id: `orders-${platform?.value}-${tenant?.value}-${Date.now()}`,
            type: 'orders',
            platform,
            tenant,
            filters,
            displayName,
            createdAt: new Date().toISOString(),
          };
          set({ favorites: [...state.favorites, newFavorite] });
        }
      },

      isOrdersFavorite: (
        platformValue: string,
        tenantValue: string | null,
        filters: OrderFilters,
      ) => {
        const state = get();
        return state.favorites.some((fav) => {
          if (fav.type !== 'orders') return false;
          const ordersFav = fav as OrdersFavorite;
          return (
            ordersFav.platform?.value === platformValue &&
            (ordersFav.tenant?.value === tenantValue || tenantValue === null) &&
            areFiltersEqual(
              ordersFav.filters as Record<string, unknown>,
              filters as Record<string, unknown>,
            )
          );
        });
      },

      getOrdersFavorites: () => {
        const state = get();
        return state.favorites.filter(
          (fav) => fav.type === 'orders',
        ) as OrdersFavorite[];
      },

      addResourcesFavorite: (
        platform: Platform,
        tenant: Tenant | null,
        filters: ResourceFilters,
        displayName?: string,
      ) => {
        const state = get();

        const exists = state.favorites.some((fav) => {
          if (fav.type !== 'resources') return false;
          const resourcesFav = fav as ResourcesFavorite;
          return (
            resourcesFav.platform?.value === platform?.value &&
            resourcesFav.tenant?.value === tenant?.value &&
            areFiltersEqual(
              resourcesFav.filters as Record<string, unknown>,
              filters as Record<string, unknown>,
            )
          );
        });

        if (!exists) {
          const newFavorite: ResourcesFavorite = {
            id: `resources-${platform?.value}-${tenant?.value}-${Date.now()}`,
            type: 'resources',
            tenant,
            filters,
            platform,
            displayName,
            createdAt: new Date().toISOString(),
          };
          set({ favorites: [...state.favorites, newFavorite] });
        }
      },

      isResourcesFavorite: (
        platformValue: string,
        tenantValue: string | null,
        filters: ResourceFilters,
      ) => {
        const state = get();
        return state.favorites.some((fav) => {
          if (fav.type !== 'resources') return false;
          const resourcesFav = fav as ResourcesFavorite;
          return (
            resourcesFav.platform?.value === platformValue &&
            (resourcesFav.tenant?.value === tenantValue ||
              tenantValue === null) &&
            resourcesFav.platform?.value === platformValue &&
            areFiltersEqual(
              resourcesFav.filters as Record<string, unknown>,
              filters as Record<string, unknown>,
            )
          );
        });
      },

      getResourcesFavorites: () => {
        const state = get();
        return state.favorites.filter(
          (fav) => fav.type === 'resources',
        ) as ResourcesFavorite[];
      },

      addHeadingClientsFavorite: (
        platform: Platform,
        filters: HeadingClientFilters,
        displayName?: string,
      ) => {
        const state = get();

        const exists = state.favorites.some((fav) => {
          if (fav.type !== 'heading_clients') return false;
          const headingClientsFav = fav as HeadingClientsFavorite;
          return (
            headingClientsFav.platform?.value === platform?.value &&
            areFiltersEqual(
              headingClientsFav.filters as Record<string, unknown>,
              filters as Record<string, unknown>,
            )
          );
        });

        if (!exists) {
          const newFavorite: HeadingClientsFavorite = {
            id: `heading_clients-${platform?.value}-${Date.now()}`,
            type: 'heading_clients',
            platform,
            tenant: null,
            filters,
            displayName,
            createdAt: new Date().toISOString(),
          };
          set({ favorites: [...state.favorites, newFavorite] });
        }
      },

      isHeadingClientsFavorite: (
        platformValue: string,
        filters: HeadingClientFilters,
      ) => {
        const state = get();
        return state.favorites.some((fav) => {
          if (fav.type !== 'heading_clients') return false;
          const headingClientsFav = fav as HeadingClientsFavorite;
          return (
            headingClientsFav.platform?.value === platformValue &&
            areFiltersEqual(
              headingClientsFav.filters as Record<string, unknown>,
              filters as Record<string, unknown>,
            )
          );
        });
      },

      getHeadingClientsFavorites: () => {
        const state = get();
        return state.favorites.filter(
          (fav) => fav.type === 'heading_clients',
        ) as HeadingClientsFavorite[];
      },

      addDocumentMappingFavorite: (
        platform: Platform,
        filters: DocumentMappingFilters,
        displayName?: string,
      ) => {
        const state = get();

        const exists = state.favorites.some((fav) => {
          if (fav.type !== 'document_mapping') return false;
          const documentMappingFav = fav as DocumentMappingFavorite;
          return (
            documentMappingFav.platform?.value === platform?.value &&
            areFiltersEqual(
              documentMappingFav.filters as Record<string, unknown>,
              filters as Record<string, unknown>,
            )
          );
        });

        if (!exists) {
          const newFavorite: DocumentMappingFavorite = {
            id: `document_mapping-${platform?.value}-${Date.now()}`,
            type: 'document_mapping',
            platform,
            tenant: null,
            filters,
            displayName,
            createdAt: new Date().toISOString(),
          };
          set({ favorites: [...state.favorites, newFavorite] });
        }
      },

      isDocumentMappingFavorite: (
        platformValue: string,
        filters: DocumentMappingFilters,
      ) => {
        const state = get();
        return state.favorites.some((fav) => {
          if (fav.type !== 'document_mapping') return false;
          const documentMappingFav = fav as DocumentMappingFavorite;
          return (
            documentMappingFav.platform?.value === platformValue &&
            areFiltersEqual(
              documentMappingFav.filters as Record<string, unknown>,
              filters as Record<string, unknown>,
            )
          );
        });
      },

      getDocumentMappingFavorites: () => {
        const state = get();
        return state.favorites.filter(
          (fav) => fav.type === 'document_mapping',
        ) as DocumentMappingFavorite[];
      },
    }),
    {
      name: 'pixid-favorites-storage',
      version: 1,
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as FavoritesState;

        // Migrate from version 0 to version 1: update created_at_from to created_at
        if (version === 0) {
          return {
            ...state,
            favorites: migrateFavorites(state.favorites || []),
          };
        }

        return state;
      },
    },
  ),
);

export default useFavoritesStore;
